import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Brain,
  Info,
  XCircle,
  CheckCircle,
  BarChart2,
  Trophy,
  Clock,
  HelpCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  runTransaction,
  increment,
} from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

const SpanTask = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // --------------------------------------
  //  GAME STATES & SETTINGS
  // --------------------------------------
  const [gameMode, setGameMode] = useState("intro"); // "intro", "tutorial", "play", "results"
  const [level, setLevel] = useState(1); // affects initial sequence length
  const [score, setScore] = useState(0);
  const [highScores, setHighScores] = useState([]);

  const [showInstructions, setShowInstructions] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackCorrect, setFeedbackCorrect] = useState(false);
  const [gameWasCompleted, setGameWasCompleted] = useState(false);

  // (1) THREE-STRIKE SYSTEM
  // Instead of ending the game on the first incorrect response, we allow up to 3 strikes.
  const [strikes, setStrikes] = useState(0);

  // Sequence logic
  const matrixSize = 3; // 3x3 grid
  const numSquares = matrixSize * matrixSize;

  const [sequenceLength, setSequenceLength] = useState(3);
  const [currentSequence, setCurrentSequence] = useState([]);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [isShowingSequence, setIsShowingSequence] = useState(false);

  // For user recall
  const [userInputIndex, setUserInputIndex] = useState(0);
  const [activeSquare, setActiveSquare] = useState(null);

  // Correct/incorrect attempts
  const [correctResponses, setCorrectResponses] = useState(0);
  const [incorrectResponses, setIncorrectResponses] = useState(0);

  // Reaction times
  const [reactionTimes, setReactionTimes] = useState([]);
  const lastRevealTime = useRef(Date.now());

  // Timer
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef(null);

  // Results
  const [gameStartTime, setGameStartTime] = useState(null);
  const [gameEndTime, setGameEndTime] = useState(null);

  // Tutorial
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialSequence, setTutorialSequence] = useState([0, 4, 8]);
  const [tutorialDisplayIndex, setTutorialDisplayIndex] = useState(0);
  const [tutorialShowingSequence, setTutorialShowingSequence] = useState(false);

  // Theme references
  const primaryColor = "from-blue-500 to-cyan-600";
  const primaryColorSolid = "bg-blue-500";
  const secondaryColor = "text-blue-300";
  const tertiaryColor = "bg-blue-500/10";

  // --------------------------------------
  //     FETCH HIGH SCORES FROM FIREBASE
  // --------------------------------------
  useEffect(() => {
    const fetchHighScores = async () => {
      if (!organizationId) return;
      try {
        // Reference the leaderboard document for "span-task"
        const leaderboardDocRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "span-task"
        );
        const docSnap = await getDoc(leaderboardDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Retrieve scores for the current level (converted to string)
          const scores =
            data.leaderboards && data.leaderboards[level.toString()]
              ? data.leaderboards[level.toString()]
              : [];
          setHighScores(scores);
        } else {
          setHighScores([]);
        }
      } catch (error) {
        console.error("Error fetching high scores:", error);
      }
    };

    fetchHighScores();
  }, [organizationId, level]);

  // --------------------------------------
  //             TIMER LOGIC
  // --------------------------------------
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // --------------------------------------
  //           CORE GAME LOGIC
  // --------------------------------------
  const generateSequence = useCallback(
    (length) => {
      const seq = [];
      for (let i = 0; i < length; i++) {
        seq.push(Math.floor(Math.random() * numSquares));
      }
      return seq;
    },
    [numSquares]
  );

  const startGame = () => {
    setGameMode("play");
    setIsPlaying(true);
    setScore(0);
    setCorrectResponses(0);
    setIncorrectResponses(0);
    setStrikes(0); // reset strikes
    setSequenceLength(2 + level); // base difficulty
    setGameStartTime(Date.now());
    setGameEndTime(null);
    setTimeLeft(180);

    const seq = generateSequence(2 + level);
    setCurrentSequence(seq);
    setDisplayIndex(0);
    setIsShowingSequence(true);
    setUserInputIndex(0);

    // (2) BLINKING SEQUENCE REVEAL
    revealSequence(seq);
  };

  /**
   * This function reveals the sequence one item at a time, turning each square on, then off,
   * even if the next item is the same square. That way repeated squares blink separately.
   */
  const revealSequence = (sequence, idx = 0) => {
    // If we've shown the entire sequence, stop and let user respond
    if (idx >= sequence.length) {
      setIsShowingSequence(false);
      setActiveSquare(null);
      lastRevealTime.current = Date.now();
      return;
    }

    // Highlight this item
    setActiveSquare(sequence[idx]);

    // Show it for 600 ms
    setTimeout(() => {
      // Turn off
      setActiveSquare(null);

      // Then wait 300 ms before going to next item
      setTimeout(() => {
        revealSequence(sequence, idx + 1);
      }, 300);
    }, 600);
  };

  const handleSquareClick = (index) => {
    // If the sequence is still being revealed, ignore clicks
    if (isShowingSequence) return;

    // Record reaction time
    const reaction = Date.now() - lastRevealTime.current;
    setReactionTimes((prev) => [...prev, reaction]);

    // Check if user clicked the correct next square in the sequence
    if (currentSequence[userInputIndex] === index) {
      // Correct
      setScore((prev) => prev + 10);
      setCorrectResponses((prev) => prev + 1);

      setFeedbackCorrect(true);
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 300);

      // Move to next item in the sequence
      if (userInputIndex + 1 === currentSequence.length) {
        // The user recalled the entire sequence. Generate next round
        setTimeout(() => {
          nextRound();
        }, 700);
      } else {
        setUserInputIndex((prev) => prev + 1);
      }
    } else {
      // Incorrect
      setScore((prev) => Math.max(0, prev - 5));
      setIncorrectResponses((prev) => prev + 1);

      // Update strikes
      setStrikes((prevStrikes) => {
        const newStrikes = prevStrikes + 1;

        if (newStrikes >= 3) {
          // On third strike, end game
          endGame();
        } else {
          // Otherwise, generate a NEW sequence of the same length
          const sameLength = currentSequence.length;
          const newSeq = generateSequence(sameLength);

          setCurrentSequence(newSeq);
          setUserInputIndex(0);
          setDisplayIndex(0);
          setIsShowingSequence(true);
          revealSequence(newSeq);
        }

        return newStrikes;
      });

      // Show incorrect feedback
      setFeedbackCorrect(false);
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 500);
    }
  };

  const nextRound = () => {
    // Increase length by 1
    const newLength = sequenceLength + 1;
    setSequenceLength(newLength);

    const newSeq = generateSequence(newLength);
    setCurrentSequence(newSeq);
    setUserInputIndex(0);

    setDisplayIndex(0);
    setIsShowingSequence(true);

    revealSequence(newSeq);
  };

  const endGame = () => {
    setIsPlaying(false);
    setGameEndTime(Date.now());
    setGameMode("results");

    const maxCompleted = sequenceLength >= 15; // or pick a different threshold
    const noStrikes = strikes < 3 && timeLeft > 0;

    // ✅ Only mark as completed if they reached high enough + didn’t rage quit
    const wasCompleted = maxCompleted || noStrikes;

    setGameWasCompleted(wasCompleted);
  };

  // --------------------------------------
  //           SAVE SCORE
  // --------------------------------------
  const saveScore = async () => {
    if (!organizationId || !user?.email) return;

    try {
      // 🧠 Resolve userName with fallbacks
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }

      // ⏱️ Average RT and game length
      const averageReactionTime =
        reactionTimes.length > 0
          ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
          : 0;

      const gameLength = gameEndTime - gameStartTime;

      // 📈 Determine longest sequence
      const longestSequence =
        sequenceLength > currentSequence.length
          ? sequenceLength
          : currentSequence.length;

      // 🧱 Unified gameData object
      const gameData = {
        gameType: "span-task",
        gameId: "span-task",
        mode: "single",
        userId: user.email,
        userName,
        score,
        level,
        correctResponses,
        incorrectResponses,
        averageReactionTime,
        gameLength,
        longestSequence,
        timestamp: new Date(),
      };

      // 🔗 Firebase references
      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        "span-task"
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      // 🧠 Custom comparator: score > longestSequence
      const simpleComparator = (a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return b.longestSequence - a.longestSequence;
      };

      // 🔁 Transaction for leaderboard + user score
      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };

        const levelKey = level.toString();
        let currentLeaderboard = leaderboardData.leaderboards[levelKey] || [];

        if (
          currentLeaderboard.length < 5 ||
          simpleComparator(
            gameData,
            currentLeaderboard[currentLeaderboard.length - 1]
          ) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(simpleComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);
          leaderboardData.leaderboards[levelKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, {
          gameScore: increment(score),
        });
      });

      // 💾 Save to gameHistory
      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );

      await setDoc(gameHistoryRef, gameData);

      // 🔄 Reset game state
      setGameMode("intro");
      setScore(0);
    } catch (error) {
      console.error("Error saving score:", error);
    }
  };

  // --------------------------------------
  //         TUTORIAL LOGIC
  // --------------------------------------
  const startTutorial = () => {
    setGameMode("tutorial");
    setTutorialStep(0);
    setTutorialSequence([0, 4, 8]);
    setTutorialDisplayIndex(0);
    setTutorialShowingSequence(false);
  };

  const advanceTutorial = () => {
    if (tutorialStep < 2) {
      setTutorialStep((prev) => prev + 1);
    } else {
      setGameMode("intro");
    }
  };

  const revealTutorial = () => {
    setTutorialShowingSequence(true);
    setTutorialDisplayIndex(0);

    // Simple "blink" approach for tutorial, similar to revealSequence
    const tutorialReveal = (idx = 0) => {
      if (idx >= tutorialSequence.length) {
        setTutorialShowingSequence(false);
        setActiveSquare(null);
        return;
      }

      setActiveSquare(tutorialSequence[idx]);
      setTimeout(() => {
        setActiveSquare(null);
        setTimeout(() => {
          tutorialReveal(idx + 1);
        }, 300);
      }, 600);
    };

    tutorialReveal(0);
  };

  // --------------------------------------
  //             RENDER
  // --------------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>

          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-blue-300">
              Memory Span
            </h1>
            <div className="p-2 bg-blue-500/10 rounded-full">
              <Brain className="text-blue-400" size={20} />
            </div>
          </div>

          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Instructions Modal */}
        <AnimatePresence>
          {showInstructions && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-slate-800 rounded-xl p-4 sm:p-6 max-w-2xl w-full border border-slate-700 max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <Brain className="text-blue-400" size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      How Memory Span Works
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowInstructions(false)}
                    className="p-2 bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                  >
                    <XCircle size={20} />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <h3 className="text-blue-300 font-medium mb-2">
                      Game Overview
                    </h3>
                    <p className="text-slate-300">
                      Memory Span challenges you to recall an ever-lengthening
                      sequence of squares in the correct order. This exercise
                      specifically trains your working memory capacity, which is
                      often impacted by substance use.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-blue-300 font-medium mb-2">
                      How to Play
                    </h3>
                    <ol className="list-decimal list-inside space-y-2 text-slate-300">
                      <li>Watch as each square lights up in sequence.</li>
                      <li>When prompted, tap them in the exact order shown.</li>
                      <li>If correct, the sequence grows by one square!</li>
                      <li>
                        If you make a mistake, you get a strike. Three strikes
                        ends the game.
                      </li>
                    </ol>
                  </div>

                  <div>
                    <h3 className="text-blue-300 font-medium mb-2">
                      Cognitive Benefits
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li>
                        Increases working memory span,important for everyday
                        functioning in recovery.
                      </li>
                      <li>
                        Reinforces attention and focus, potentially improving
                        frontal-lobe function.
                      </li>
                      <li>Provides immediate feedback and motivation.</li>
                    </ul>
                  </div>
                </div>

                <button
                  onClick={() => setShowInstructions(false)}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 text-white py-3 rounded-lg font-medium hover:from-blue-600 hover:to-cyan-700 transition-colors"
                >
                  Got it!
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Game Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* INTRO SCREEN */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Brain size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Memory Span
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  Test and improve your working memory by recalling sequences of
                  squares. Each round gets tougher. You have three strikes—good
                  luck!
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-blue-500/10 rounded-full mb-2">
                    <Brain size={24} className="text-blue-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Working Memory
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Rebuild cognitive skills essential in daily life.
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-blue-500/10 rounded-full mb-2">
                    <Clock size={24} className="text-blue-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Recommended Time
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    5-10 minutes per session
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-blue-500/10 rounded-full mb-2">
                    <Trophy size={24} className="text-blue-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">High Scores</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Compare your performance with others.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty Level
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {[1, 2, 3].map((n) => (
                      <button
                        key={n}
                        onClick={() => setLevel(n)}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                          level === n
                            ? "bg-gradient-to-r from-blue-500 to-cyan-600 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        Level {n}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={startTutorial}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>

                  <button
                    onClick={startGame}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {/* High Scores Display */}
              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Level
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Time
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Longest
                          </th>

                          <th className="py-2 px-4 text-right text-slate-300">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((item, index) => {
                          let formattedName = item.userName;
                          if (item.userName && item.userName.includes(" ")) {
                            const nameParts = item.userName.split(" ");
                            const firstName = nameParts[0];
                            const lastInitial = nameParts[1]
                              ? nameParts[1][0] + "."
                              : "";
                            formattedName = `${firstName} ${lastInitial}`;
                          }

                          return (
                            <tr
                              key={item.id}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-slate-300">
                                {item.level}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {item.averageReactionTime
                                  ? Math.round(item.averageReactionTime) + " ms"
                                  : "N/A"}
                              </td>
                              <td className="py-2 px-4 text-center text-white">
                                {item.longestSequence ?? "—"}
                              </td>

                              <td className="py-2 px-4 text-right text-blue-300 font-medium">
                                {item.score}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TUTORIAL SCREEN */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Tutorial: Memory Span
                </h2>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  {tutorialStep === 0 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        In this exercise, you'll see a sequence of squares light
                        up one at a time. After the sequence finishes, click
                        them in the correct order. You have up to three strikes.
                      </p>
                      <p className="text-slate-300">
                        Let's walk through a quick demo sequence.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 1 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        The squares lit up in a specific order. Once revealed,
                        you'd tap them in exactly that order. Each successful
                        recall adds another square to the next round. If you
                        make a mistake, you lose a strike. Three strikes ends
                        the game.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 2 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        You're ready to try the real game. Good luck!
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-6 items-center">
                {/* Tutorial Grid */}
                <div className="flex-1 flex justify-center">
                  <div className="grid grid-cols-3 gap-2 bg-slate-900/60 p-4 rounded-xl border border-slate-700">
                    {[...Array(numSquares)].map((_, index) => (
                      <div
                        key={index}
                        className={`w-16 h-16 md:w-20 md:h-20 rounded-lg border-2 transition-colors ${
                          tutorialShowingSequence &&
                          tutorialSequence[tutorialDisplayIndex] === index
                            ? `${primaryColorSolid} border-blue-400`
                            : "bg-slate-800/60 border-slate-700 hover:border-slate-600"
                        }`}
                      ></div>
                    ))}
                  </div>
                </div>

                {/* Tutorial Controls */}
                <div className="flex-1 space-y-4">
                  {tutorialStep === 0 && (
                    <button
                      onClick={revealTutorial}
                      className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                    >
                      Show Tutorial Sequence
                    </button>
                  )}

                  <div className="flex justify-between">
                    {tutorialStep > 0 ? (
                      <button
                        onClick={() => setTutorialStep((prev) => prev - 1)}
                        className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                      >
                        Previous
                      </button>
                    ) : (
                      <button
                        onClick={() => setGameMode("intro")}
                        className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                      >
                        Back
                      </button>
                    )}

                    <button
                      onClick={advanceTutorial}
                      className="py-2 px-4 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-lg transition-colors"
                    >
                      {tutorialStep < 2 ? "Next" : "Done"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PLAY SCREEN */}
          {gameMode === "play" && (
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-blue-500/20 to-cyan-600/20 px-4 py-2 rounded-lg">
                  <span className="text-blue-300 font-medium">
                    Level: {level}
                  </span>
                </div>

                <div className="flex items-center gap-4">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>

                  <div className="bg-gradient-to-r from-blue-500/20 to-cyan-600/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>

              {/* Strike Indicators */}
              <div className="flex justify-center mb-4">
                {[...Array(3)].map((_, idx) => {
                  const isStrike = idx < strikes;
                  return (
                    <div
                      key={idx}
                      className={`mx-1 p-2 rounded-full ${
                        isStrike
                          ? "bg-red-500/30 text-red-400"
                          : "bg-slate-700/20 text-slate-500"
                      }`}
                    >
                      <XCircle size={24} />
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-center justify-center">
                {/* Game Grid */}
                <div className="relative">
                  <div className="grid grid-cols-3 gap-3 bg-slate-900/60 p-5 rounded-xl border border-slate-700">
                    {[...Array(numSquares)].map((_, index) => (
                      <div
                        key={index}
                        onClick={() => handleSquareClick(index)}
                        className={`w-20 h-20 md:w-24 md:h-24 rounded-lg border-2 transition-colors cursor-pointer ${
                          isShowingSequence
                            ? // If we are still revealing, highlight the active square
                              activeSquare === index
                              ? `${primaryColorSolid} border-blue-400`
                              : "bg-slate-800/60 border-slate-700"
                            : // If reveal is done, squares are clickable
                              "bg-slate-800/60 border-slate-700 hover:border-slate-600"
                        }`}
                      ></div>
                    ))}
                  </div>

                  {/* Feedback Overlay */}
                  <AnimatePresence>
                    {showFeedback && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.2 }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <div
                          className={`p-6 rounded-full ${
                            feedbackCorrect
                              ? "bg-green-500/20 text-green-400"
                              : "bg-red-500/20 text-red-400"
                          }`}
                        >
                          {feedbackCorrect ? (
                            <CheckCircle size={60} />
                          ) : (
                            <XCircle size={60} />
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Info Panel */}
                <div className="md:w-1/3 space-y-6">
                  <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                    <h3 className="text-white font-medium mb-3">
                      Game Progress
                    </h3>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-400 text-sm">Round Size</span>
                      <span className="text-white font-medium">
                        {currentSequence.length}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-cyan-600"
                        style={{
                          width: `${
                            (userInputIndex / currentSequence.length) * 100
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            "Are you sure you want to end the game early?"
                          )
                        ) {
                          endGame();
                        }
                      }}
                      className="w-full py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors text-sm"
                    >
                      End Game
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* RESULTS SCREEN */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You reached a sequence length of {currentSequence.length}.
                </p>
              </div>

              <div className="mt-2 p-3 bg-slate-800/60 rounded-lg max-w-xl mx-auto mb-2">
                <p className="text-sm text-slate-300">
                  <span className="text-green-400 font-medium">
                    Correct Responses
                  </span>
                  : how many squares you tapped correctly over all attempts.
                  <br />
                  <span className="text-red-400 font-medium">
                    Incorrect Responses
                  </span>
                  : how many mistakes occurred before the third strike.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-blue-400" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-blue-300">
                        {score}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium">{level}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Longest Sequence</span>
                      <span className="text-white">
                        {sequenceLength > currentSequence.length
                          ? sequenceLength
                          : currentSequence.length}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Correct Responses</span>
                      <span className="text-green-400">{correctResponses}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">
                        Incorrect Responses
                      </span>
                      <span className="text-red-400">{incorrectResponses}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Avg. Reaction Time</span>
                      <span className="text-white">
                        {reactionTimes.length > 0
                          ? `${Math.round(
                              reactionTimes.reduce((a, b) => a + b, 0) /
                                reactionTimes.length
                            )} ms`
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-blue-400" />
                    Performance Analysis
                  </h3>
                  <div className="space-y-5">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">Accuracy</span>
                        <span className="text-white text-sm">
                          {correctResponses + incorrectResponses > 0
                            ? `${Math.round(
                                (correctResponses /
                                  (correctResponses + incorrectResponses)) *
                                  100
                              )}%`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-cyan-600"
                          style={{
                            width:
                              correctResponses + incorrectResponses > 0
                                ? `${
                                    (correctResponses /
                                      (correctResponses + incorrectResponses)) *
                                    100
                                  }%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-white font-medium mb-2">
                        Cognitive Benefits
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-blue-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-blue-400" />
                          </div>
                          <span className="text-slate-300">
                            Improved working memory capacity
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-blue-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-blue-400" />
                          </div>
                          <span className="text-slate-300">
                            Enhanced attention and focus
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-blue-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-blue-400" />
                          </div>
                          <span className="text-slate-300">
                            Strengthened neural pathways in the prefrontal
                            cortex
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("intro")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                {gameWasCompleted && (
                  <button
                    onClick={saveScore}
                    className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <Trophy size={18} />
                    Save Score
                  </button>
                )}

                <button
                  onClick={startGame}
                  className="py-3 px-6 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpanTask;
